import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

const BarChartComponent = ({
    data,
    title = "Bar Chart",
    dataKey = "value",
    nameKey = "name",
    loading = false,
    height = 300,
    barColor = '#3b82f6',
    showValues = true,
    orientation = 'vertical' // 'vertical' or 'horizontal'
}) => {
    if (loading) {
        return (
            <div className="w-full rounded-2xl flex flex-col p-5" style={{
                background: "#2d3347",
                boxShadow: '0 2px 12px #0004',
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#facc15', textAlign: 'center' }}>
                    {title}
                </div>
                <div style={{ color: '#888', textAlign: 'center' }}>
                    جاري تحميل البيانات...
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="w-full rounded-2xl flex flex-col p-5" style={{
                background: "#2d3347",
                boxShadow: '0 2px 12px #0004',
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#facc15', textAlign: 'center' }}>
                    {title}
                </div>
                <div style={{ color: '#888', textAlign: 'center' }}>
                    لا توجد بيانات متاحة
                </div>
            </div>
        );
    }

    // Color mapping based on value ranges
    const getBarColor = (value) => {
        if (value >= 70) return '#6aa84f'; // Green for good performance
        if (value >= 40) return '#fd7e00'; // Orange for medium performance
        return '#f44336'; // Red for poor performance
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'rgba(32, 42, 58, 0.95)',
                    border: '1px solid #444',
                    borderRadius: 8,
                    padding: '12px 16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    color: '#fff',
                    fontSize: 14,
                    backdropFilter: 'blur(10px)',
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#facc15' }}>
                        {label}
                    </p>
                    <p style={{ margin: 0, color: payload[0].fill }}>
                        القيمة: {Math.round(payload[0].value)}%
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="w-full rounded-2xl flex flex-col p-5" style={{
            background: "#2d3347",
            boxShadow: '0 2px 12px #0004',
            minHeight: height,
        }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#facc15', textAlign: 'center', marginBottom: 16 }}>
                {title}
            </div>

            <ResponsiveContainer width="100%" height={height - 60}>
                <BarChart
                    data={data}
                    layout={orientation === 'horizontal' ? 'horizontal' : 'vertical'}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" opacity={0.3} />

                    {orientation === 'horizontal' ? (
                        <>
                            <XAxis type="number" stroke="#888" tick={{ fill: '#fff' }} />
                            <YAxis dataKey={nameKey} type="category" stroke="#888" tick={{ fill: '#fff' }} width={120} />
                        </>
                    ) : (
                        <>
                            <XAxis dataKey={nameKey} stroke="#888" tick={{ fill: '#fff' }} />
                            <YAxis stroke="#888" tick={{ fill: '#fff' }} />
                        </>
                    )}

                    <Tooltip content={<CustomTooltip />} />

                    <Bar
                        dataKey={dataKey}
                        fill={barColor}
                        radius={[8, 8, 0, 0]}
                        animationDuration={800}
                        animationBegin={0}
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getBarColor(entry[dataKey])} />
                        ))}
                        {showValues && (
                            <LabelList
                                dataKey={dataKey}
                                position="top"
                                fill="#facc15"
                                fontSize={12}
                                fontWeight="bold"
                                formatter={(value) => `${Math.round(value)}%`}
                            />
                        )}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default BarChartComponent;
